import { Alignment, Displaying } from "@bonusx-global/bx-design-system";
import { ArrowExportOutlined } from "@bonusx-global/bx-icons";
import styled from "@emotion/styled";
import Link from "@mui/material/Link";
import type { PropsWithChildren } from "react";
import { useCallback, useMemo } from "react";
import useIsMobile from "../../../../../../../../hooks/device/useIsMobile";
import { isPathExternal } from "../../../../../../BottomNavigationBar/functions/is-path-external/is-path-external";
import { useChatbotPanel } from "../../../../../hooks/use-chatbot-panel/use-chatbot-panel";
import type { AiResponseMarkdownLinkProps } from "./ai-response-markdown-link.interface";

const LinkWithIcon = styled(Link)({
  display: Displaying.inlineFlex,
  alignItems: Alignment.center,
});

function AiResponseMarkdownLink({ href, children }: PropsWithChildren<AiResponseMarkdownLinkProps>): JSX.Element {
  const isMobile = useIsMobile();
  const { closeChatbotPanel } = useChatbotPanel();

  const url = useMemo(
    function memoizeURL() {
      if (!href) return;

      try {
        return new URL(href, window.location.origin);
      } catch (e) {
        console.debug("Invalid URL", href);
        return;
      }
    },
    [href]
  );

  const isInternalLink = url?.host === window.location.host;
  const isRelativeLink = !isPathExternal(href);

  const onClickCallback = useCallback(
    function onClick() {
      if (isMobile && isInternalLink) {
        closeChatbotPanel();
      }
    },
    [isMobile, isInternalLink, closeChatbotPanel]
  );

  return (
    <LinkWithIcon
      href={href}
      target={isInternalLink || isRelativeLink ? "_self" : "_blank"}
      onClick={onClickCallback}
      rel="noopener noreferrer"
    >
      {children}

      {!isInternalLink && <ArrowExportOutlined />}
    </LinkWithIcon>
  );
}

export default AiResponseMarkdownLink;
